/** Offline-tolerant queue for completed daily results.
 *
 *  Scoring and streaks are SERVER-SIDE and must stay that way — the leaderboard
 *  is only trustworthy because the client never computes points. So when a
 *  completion can't reach the server (offline), we hold the raw result here and
 *  POST it verbatim on reconnect; the server computes the score as always. */
import { api } from '../api/client';
import type { DailyResultIn, DailyResultOut } from '../api/types';

const KEY = 'sudoku:pending-results';

export interface PendingResult {
  date: string;
  payload: DailyResultIn;
}

function load(): PendingResult[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as PendingResult[]) : [];
  } catch {
    return [];
  }
}

function save(items: PendingResult[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* storage full / disabled — the result is lost, but never fabricated */
  }
}

function idOf(date: string, difficulty: string): string {
  return `${date}:${difficulty}`;
}

/** Queue a result, replacing any earlier pending one for the same day+bracket. */
export function enqueueResult(item: PendingResult): void {
  const items = load().filter(
    (p) => idOf(p.date, p.payload.difficulty) !== idOf(item.date, item.payload.difficulty),
  );
  items.push(item);
  save(items);
}

export function hasPending(date: string, difficulty: string): boolean {
  return load().some((p) => idOf(p.date, p.payload.difficulty) === idOf(date, difficulty));
}

/** Drain the queue, POSTing each held result. Items that still fail (offline /
 *  server error) are kept for the next attempt. Returns the server-computed
 *  results keyed by `<date>:<difficulty>` so the UI can show a real score. */
export async function flushResults(): Promise<Map<string, DailyResultOut>> {
  const items = load();
  if (items.length === 0) return new Map();

  const remaining: PendingResult[] = [];
  const done = new Map<string, DailyResultOut>();

  for (const item of items) {
    try {
      const out = await api<DailyResultOut>(`/daily/${item.date}/result`, {
        method: 'POST',
        body: item.payload,
      });
      done.set(idOf(item.date, item.payload.difficulty), out);
    } catch {
      remaining.push(item);
    }
  }

  save(remaining);
  return done;
}
